import {
  getDimensionsFromMap,
  useElement,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import {
  getReportOverlayMP,
  ReportOverlayResponse,
  useSession,
} from '@ais-ui/websockets';
import { debounce } from '@ais-ui/utils';
import { Projection, transformExtent } from 'ol/proj';
import Static from 'ol/source/ImageStatic';
import { useCallback, useEffect, useState } from 'react';
import { useReportContext } from './ReportContext';
import Map from 'ol/Map';
import { Session } from '@ais-ui/auth';
import { useTimeZoneContext } from '@ais-ui/utils';

export async function fetchReportOverlay(
  map: Map,
  displayProjection: Projection | string,
  dataProjection: Projection | string,
  jobId: string,
  dbSession: Session,
  timeZone: string,
) {
  const dimensions = getDimensionsFromMap(
    map,
    displayProjection,
    dataProjection,
  );
  if (!dimensions) return null;

  const { center, zoomLevel, size, northEast, southWest } = dimensions;
  const newOverlayParams = {
    jobUuid: jobId,
    centerLat: center.lat,
    centerLon: center.lng,
    zoomLevel: Math.round(zoomLevel),
    width: size.width,
    height: size.height,
    leftLon: southWest.lng,
    rightLon: northEast.lng,
    legendX: 0,
    legendY: 0,
  };

  return await getReportOverlayMP(dbSession, newOverlayParams, timeZone);
}

export const useFetchOverlayData = () => {
  const { dbSession } = useSession();
  const { timeZone } = useTimeZoneContext();
  const { map, displayProjection, dataProjection } = useOpenLayersContext();
  const { setLegend, reportOverlayRef } = useReportContext();

  const fetchOverlayData = useCallback(
    async (
      jobId: string,
      hasZoomed: boolean,
    ): Promise<ReportOverlayResponse | null> => {
      if (!dbSession) {
        console.warn(
          'Skipping overlay data fetch: dbSession is not available (possibly reconnecting)',
        );
        return null;
      }

      if (!hasZoomed) return null;

      try {
        const reportOverlay = await fetchReportOverlay(
          map,
          displayProjection,
          dataProjection,
          jobId,
          dbSession,
          timeZone,
        );

        if (!reportOverlay) return null;

        setLegend(reportOverlay.legend);

        const mapExtent = map.getView().calculateExtent(map.getSize());
        const imageUrl = `data:image/png;base64,${reportOverlay.overlay}`;

        reportOverlayRef.current.setSource(
          new Static({
            url: imageUrl,
            imageExtent: mapExtent,
          }),
        );

        return reportOverlay;
      } catch (error) {
        // Handle DataNotLoaded errors gracefully - this can happen when switching reports
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        if (
          errorMessage.includes('DataNotLoaded') ||
          errorMessage.includes('InternalError')
        ) {
          console.warn(
            '[useMapReportOverlay] Overlay fetch failed (likely report switch):',
            errorMessage,
          );
          return null;
        }
        throw error;
      }
    },
    [
      dbSession,
      map,
      displayProjection,
      dataProjection,
      setLegend,
      reportOverlayRef,
    ],
  );

  return fetchOverlayData;
};

export const useMapReportOverlay = (
  jobId: string,
  extent: number[] | undefined,
) => {
  const [hasZoomed, setHasZoomed] = useState(false);
  const fetchOverlayData = useFetchOverlayData();
  const { reportOverlayRef } = useReportContext();
  const { map, dataProjection } = useOpenLayersContext();

  // Reset hasZoomed when jobId changes
  useEffect(() => {
    setHasZoomed(false);
  }, [jobId]);

  useEffect(() => {
    if (!extent || hasZoomed) return;

    // We have seen the backend give extent of [360, 360, 360, 360]
    if (extent.length === 4 && extent.every((value) => value === 360)) {
      console.warn('Invalid extent received from backend:', extent);
      return;
    }

    const transformedExtent = transformExtent(
      extent,
      dataProjection,
      map.getView().getProjection(),
    );
    map
      .getView()
      .fit(transformedExtent, { padding: [100, 200, 100, 0], duration: 200 });
    setHasZoomed(true);
  }, [map, extent, hasZoomed, setHasZoomed, dataProjection]);

  useEffect(() => {
    const debouncedFetchOverlayData = debounce(
      () => fetchOverlayData(jobId, hasZoomed),
      100,
    );

    const handleMapChange = () => debouncedFetchOverlayData();
    map.on('moveend', handleMapChange);

    return () => {
      map.un('moveend', handleMapChange);
      debouncedFetchOverlayData.cancel(); // cancel any pending debounced calls on cleanup
    };
  }, [map, jobId, hasZoomed, fetchOverlayData]);

  useElement(reportOverlayRef.current, 'Report Overlay');
};
